import type { GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import type { BonusCampaign, BonusCampaignType } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { createEditColumn } from '../../helpers/columns';
import { defaultColumnsBonuses } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import { createColumn, formatSnakeCaseString } from '../../utils';
import type { ApiService } from '../../utils/api';
import { FormTypes } from '../modals/FormModal';
import TableTemplate from '../templates/TableTemplate';

const defaultCashbackColumns = { ...defaultColumnsBonuses, rewardType: true };

type BonusPageProps = {
  type: BonusCampaignType;
  formType?: FormTypes;
  endpoint?: string;
  apiService?: ApiService;
  queryType?: 'feed' | 'wallet-cashback';
};

const BonusPage = ({
  type,
  formType,
  endpoint = API_ROUTES.BONUS_CAMPAIGNS,
  apiService = 'feed',
  queryType = 'feed',
}: BonusPageProps) => {
  const {
    data: bonusData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<BonusCampaign>(
    endpoint,
    {
      page: 1,
      limit: 25,
      ...(queryType === 'feed' && { type }),
    },
    undefined,
    apiService
  );

  const queryKey = queryType === 'wallet-cashback' ? QUERY_KEYS.cashbackBonus : QUERY_KEYS.bonus;
  const { deleteData } = useMutateData(endpoint, [queryKey, type], apiService);

  const handleSearch = (value: string | null) => {
    changeQuery(queryType === 'wallet-cashback' ? { name: value } : { search: value });
  };

  const handleSort = useCallback(
    (model: GridSortModel) => {
      const sortModelItem = model[0];

      if (!sortModelItem) {
        changeQuery({ sort: undefined, order: undefined, sortKey: undefined, sortOrder: undefined });
        return;
      }

      const sortOrder = (sortModelItem.sort || 'asc').toUpperCase();
      changeQuery(
        queryType === 'wallet-cashback'
          ? { sortKey: sortModelItem.field, sortOrder }
          : { sort: sortModelItem.field, order: sortOrder }
      );
    },
    [changeQuery, queryType]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('id', 'ID', {
        sortable: false,
      }),
      createColumn('name', 'Name', {
        alwaysVisible: true,
        minWidth: 150,
      }),
      createColumn('product', 'Product', {
        alwaysVisible: true,
        valueFormatter: (value, _row, _column, _apiRef) => formatSnakeCaseString(value),
      }),
      ...(queryType === 'wallet-cashback'
        ? [
            createColumn('rewardType', 'Reward Type', {
              alwaysVisible: true,
              minWidth: 150,
              sortable: false,
              valueFormatter: (value) => (value ? formatSnakeCaseString(value) : ''),
            }),
          ]
        : []),
      createColumn('status', 'Status', {
        alwaysVisible: true,
        valueFormatter: (value, _row, _column, _apiRef) => formatSnakeCaseString(value),
      }),
      createColumn('description', 'Description', { sortable: false }),
      createEditColumn<BonusCampaign>({
        deleteData,
        columnName: 'Actions',
      }),
    ],
    [deleteData, queryType]
  );

  const location = useLocation();
  const selectionBonus = location.pathname.includes('/selections');

  return (
    <TableTemplate
      rows={bonusData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={bonusData?.count || 0}
      createItemBtnLabel='Add new bonus'
      defaultVisibleColumns={queryType === 'wallet-cashback' ? defaultCashbackColumns : defaultColumnsBonuses}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      formType={formType}
      disableFilter={selectionBonus}
    />
  );
};

export default BonusPage;
